import {
  IPagedCollection,
  IStatus,
  ICreateAssetStatusRequest,
  ICreateAssetStatusResponse,
  IUpdateAssetStatusRequest,
  IEntityLocation,
} from './assetServices';

import { internalFetch } from '../utils/fetchHelper';

export interface IRelatedAsset {
  id: number;
  uuid: string;
  code: string;
  name: string;
  createdByContractor?: boolean;
}

export interface IWork {
  id: number;
  issueId: number;
  code: string;
  description: string;
  specialInstruction?: string | null;
  status: string;
  statusId: number;
  metadataName?: string;
  statusDescription: string;
  createdAt: string;
  zoneId?: number;
  assetId?: number;
  assetCodeAndName?: string;
  orderId?: number;
  relatedAssets?: IRelatedAsset[];
  workTypeId?: number | null;
  tenantId?: number;
}

export interface IWorkComment {
  id: number;
  content: string;
  createByUserName?: string;
  createdAt?: Date;
  createdAtFormatted?: string;
  modifiedByUserName?: string;
  modifiedAt?: Date;
  modifiedAtFormatted?: string;
}

export interface IWorkImage {
  id: number;
  createByUserName?: string;
  createdAt?: Date;
  createdAtFormatted?: string;
  imageThumbnail: string;
  imageUrl: string;
  type?: string;
  caption?: string | null;
}

export interface ILocation {
  latitude: number;
  longitude: number;
}

export interface IWorkImageContent {
  imageContent: string;
  location: ILocation | null;
  note?: string;
  analysis?: string;
}

export interface ICreateWorkImageRequest {
  workId: number;
  imageContent: string;
  type: string;
  note?: string;
  location?: ILocation | null;
  caption?: string;
}

export interface ICreateWorkImageResponse {
  id: number;
}

export interface IWorkRequest {
  assetId?: number;
  relatedAssetIds?: number[];
  zoneId?: number;
  description?: string;
  specialInstruction?: string | null;
  zoneAssetCatId?: number;
  issueTypeId?: number;
  statusId?: number;
  otherInfo?: Record<string, unknown>;
  metadataName?: string;
  workTypeId?: number | null;
}

export interface IUpdateWorkRequest {
  description?: string;
  specialInstruction?: string | null;
  otherInfo?: Record<string, unknown>;
  status?: string;
  statusId?: number;
  assetId?: number;
  relatedAssetIds?: number[];
  issueId?: number;
  zoneId?: number;
  metadataName?: string;
}

export interface IWorkAssignmentRequest {
  contractorId: number;
  workId: number;
  description: string;
}

export interface IUpdateWorkAssignmentsRequest {
  workIds: number[];
  description?: string;
}

export interface ICreateWorkCommentRequest {
  workId: number;
  content?: string;
}

export interface ICreateWorkCommentResponse {
  id: number;
  createdAt: Date;
  createByUserName: string;
}

export interface IUpdateWorkCommentRequest {
  commentId: number;
  content: string;
}

export interface IUpdateWorkCommentResponse {
  commentId: number;
  modifiedByUserName: string;
  modifiedAt: Date;
}

export interface IWorkAssignmentResponse {
  workAssignmentId: number;
}

export interface IWorkResponse {
  workId: number;
}

export interface IStatusPermission {
  groupId: number | null;
  userId: number | null;
  tenantId: number | null;
}

export interface IWorkStatus extends IStatus {
  isCompleted?: boolean;
  IsCompleted?: boolean;
  allowReportDelivery?: boolean;
  deliverReportAutomatically?: boolean;
  useOnFormFinish?: boolean | null;
  showAsAction?: boolean;
  actionLabel?: string;
  permissions?: IStatusPermission[];
}

export interface ICreateOrderStatusRequest extends ICreateAssetStatusRequest {
  isCompleted?: boolean;
  allowReportDelivery?: boolean;
  deliverReportAutomatically?: boolean;
  useOnFormFinish?: boolean | null;
  showAsAction?: boolean;
  actionLabel?: string;
  clientStatusGroupId?: number | null;
  managerStatusGroupId?: number | null;
  contractorStatusGroupId?: number | null;
  permissions?: IStatusPermission[];
}

export interface IUpdateOrderStatusRequest extends IUpdateAssetStatusRequest {
  isCompleted?: boolean;
  allowReportDelivery?: boolean;
  deliverReportAutomatically?: boolean;
  useOnFormFinish?: boolean | null;
  showAsAction?: boolean;
  actionLabel?: string;
  clientStatusGroupId?: number | null;
  managerStatusGroupId?: number | null;
  contractorStatusGroupId?: number | null;
  permissions?: IStatusPermission[];
}

export interface IOrderStatus extends IWorkStatus {
  clientStatusGroupId?: number | null;
  managerStatusGroupId?: number | null;
  contractorStatusGroupId?: number | null;
}

export const getWork = async (
  abortController: AbortController,
  workId: number
): Promise<IWork> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/work?workId=${workId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getWorkComments = async (
  abortController: AbortController,
  workId: number,
  pageSize?: number,
  pageNo?: number,
  orderBy?: string,
  search?: string
): Promise<IPagedCollection<IWorkComment>> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/workComments?workId=${workId}${
      pageSize ? `&pageSize=${pageSize}` : ''
    }${pageNo ? `&pageNo=${pageNo}` : ''}${
      orderBy ? `&orderBy=${orderBy}` : ''
    }${search ? `&search=${search}` : ''}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getWorkImages = async (
  abortController: AbortController,
  workId: number,
  pageSize?: number,
  pageNo?: number
): Promise<IPagedCollection<IWorkImage>> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/workImages?workId=${workId}${
      pageSize ? `&pageSize=${pageSize}` : ''
    }${pageNo ? `&pageNo=${pageNo}` : ''}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getWorkImage = async (
  abortController: AbortController,
  imageId: number
): Promise<IWorkImageContent> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/workImage?workImageId=${imageId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const createWorkImage = async (
  abortController: AbortController,
  data: ICreateWorkImageRequest
): Promise<ICreateWorkImageResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/workImage?code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'POST',
    abortController,
    JSON.stringify(data)
  );
};

export const deleteWorkImage = async (
  abortController: AbortController,
  workImageId: number
): Promise<IWorkImageContent> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/workImage?imageId=${workImageId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'DELETE',
    abortController
  );
};

export const deleteWork = async (
  abortController: AbortController,
  workId: number
): Promise<void> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/work?workId=${workId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'DELETE',
    abortController,
    undefined,
    true
  );
};

export const getWorkLocations = async (
  abortController: AbortController,
  workId: number,
  pageSize: number,
  pageNo: number
): Promise<IPagedCollection<IEntityLocation>> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/workLocations?workId=${workId}&pageSize=${pageSize}&pageNo=${pageNo}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getWorkComment = async (
  abortController: AbortController,
  commentId: number
): Promise<IWorkComment> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/workComment?commentId=${commentId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getWorkAssignment = async (
  abortController: AbortController,
  contractorId: number,
  pageSize: number,
  pageNo: number,
  search?: string
): Promise<IPagedCollection<IWork>> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/workAssignment?contractorId=${contractorId}&pageSize=${pageSize}&pageNo=${pageNo}${
      search ? `&search=${search}` : ''
    }&code=${import.meta.env.VITE_BACKEND_API_KEY}`,
    'GET',
    abortController
  );
};

export const createWork = async (
  abortController: AbortController,
  data: IWorkRequest,
  issueId?: number
): Promise<IWorkResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/work?issueId=${issueId}`,
    'POST',
    abortController,
    JSON.stringify(data)
  );
};

export const updateWork = async (
  abortController: AbortController,
  workId: number,
  data: IUpdateWorkRequest
): Promise<IWorkResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/work?id=${workId}`,
    'PUT',
    abortController,
    JSON.stringify(data)
  );
};

export const createWorkAssignment = async (
  abortController: AbortController,
  data: IWorkAssignmentRequest
): Promise<IWorkAssignmentResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/workAssignment`,
    'POST',
    abortController,
    JSON.stringify(data)
  );
};

export const updateWorkAssignments = async (
  abortController: AbortController,
  contractorId: number,
  data: IUpdateWorkAssignmentsRequest
): Promise<void> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/workAssignments?contractorId=${contractorId}`,
    'PUT',
    abortController,
    JSON.stringify(data),
    true
  );
};

export const createWorkComment = async (
  abortController: AbortController,
  data: ICreateWorkCommentRequest
): Promise<ICreateWorkCommentResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/workComment`,
    'POST',
    abortController,
    JSON.stringify(data)
  );
};

export const updateWorkComment = async (
  abortController: AbortController,
  data: IUpdateWorkCommentRequest
): Promise<IUpdateWorkCommentResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/workComment`,
    'PUT',
    abortController,
    JSON.stringify(data)
  );
};

export const getWorkStatuses = async (
  abortController: AbortController
): Promise<IWorkStatus[]> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/workStatuses`,
    'GET',
    abortController
  );
};

export const updateWorkStatus = async (
  abortController: AbortController,
  workStatusId: number,
  workStatus: {
    clientLabel?: string | null;
    managerLabel?: string | null;
    contractorLabel?: string | null;
    color?: string;
    icon?: string;
    deliverReportAutomatically: boolean;
    permissions?: IStatusPermission[];
  }
): Promise<number> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/workStatusById?workStatusId=${workStatusId}`,
    'PUT',
    abortController,
    JSON.stringify(workStatus)
  );
};

export const getOrderStatuses = async (
  abortController: AbortController
): Promise<IOrderStatus[]> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/orderStatuses`,
    'GET',
    abortController
  );
};

export const createOrderStatus = async (
  abortController: AbortController,
  orderStatus: ICreateOrderStatusRequest
): Promise<ICreateAssetStatusResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/orderStatus`,
    'POST',
    abortController,
    JSON.stringify(orderStatus)
  );
};

export const updateOrderStatus = async (
  abortController: AbortController,
  orderStatusId: number,
  orderStatus: IUpdateOrderStatusRequest
): Promise<number> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/orderStatus?orderStatusId=${orderStatusId}`,
    'PUT',
    abortController,
    JSON.stringify(orderStatus)
  );
};

export const getWorks = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  search?: string,
  filters?: string
): Promise<IPagedCollection<IWork>> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/works?pageSize=${pageSize}&pageNo=${pageNo}${
      orderBy ? `&orderBy=${orderBy}` : ''
    }${search ? `&search=${search}` : ''}${
      filters ? `&filters=${encodeURIComponent(filters)}` : ''
    }&code=${import.meta.env.VITE_BACKEND_API_KEY}`,
    'GET',
    abortController
  );
};

export const searchWorks = async (
  abortController: AbortController,
  search: string
): Promise<IWork[]> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/searchWorks?search=${search}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getWorksOfIssue = async (
  abortController: AbortController,
  issueId: number
): Promise<IWork[]> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/worksOfIssue?issueId=${issueId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const updateStatusOfWork = async (
  abortController: AbortController,
  workCode: string,
  workStatus: string,
  workStatusId: number,
  workDescription: string
) => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/work?workCode=${workCode}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'PUT',
    abortController,
    JSON.stringify({
      status: workStatus,
      statusId: workStatusId,
      description: workDescription,
    })
  );
};

export const getWorkReportFile = async (
  abortController: AbortController,
  workId: number
): Promise<Response> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/generateWorkReport?workId=${workId}`,
    'GET',
    abortController,
    undefined,
    true
  );
};

export interface IResendWorkResponse {
  workId: number;
}

export const resendWork = async (
  abortController: AbortController,
  workId: number
): Promise<IResendWorkResponse> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/resendWork?workId=${workId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'POST',
    abortController
  );
};
